import { ArrowLeft, Briefcase, ClipboardList, Search, PlusCircle, Home, ListPlus, Building, Store, TrendingUp } from "lucide-react";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { useEffect, useState } from "react";
import apiClient from "../common/apiClient";
import Cookies from "js-cookie";
import decryptResponse from "../common/decrypt";
import { useUserProfile, DEFAULT_PROPERTY_CITIES, CITY_LABELS_GU } from "../contexts/UserProfileContext";
import { FEATURES } from "../constants/feature.constants";
import { openPremiumRequiredPopup } from "../components/PremiumRequiredPopup";
import { showSessionExpiredPopup } from "../components/SessionExpiredPopup";

const bhkData = [
  { name: "1 BHK", value: 1200 }, { name: "2 BHK", value: 3400 },
  { name: "3 BHK", value: 2800 }, { name: "4+ BHK", value: 830 },
];

const subtypeData = [
  { name: "Flat", count: 4200 }, { name: "Villa", count: 1100 },
  { name: "Plot", count: 890 }, { name: "Office", count: 1540 },
  { name: "Shop", count: 500 },
];

const COLORS = ["hsl(262,83%,58%)", "hsl(270,75%,68%)", "hsl(210,100%,52%)", "hsl(38,92%,50%)"];

const BrokerB2B = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [allCounts, setAllCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const { userProfile, hasFeature, hasCityAccess, propertyAccessCities } = useUserProfile();
  const [myPropertiesCount, setMyPropertiesCount] = useState(0);
const [myRequirementsCount, setMyRequirementsCount] = useState(0);
  const [selectedCity, setSelectedCity] = useState(
    localStorage.getItem("selectedCity") || "Ahmedabad West"
  );

  // Driven by PropertyAccessCity: only the cities the user actually has access to
  // are rendered. Falls back to the default list until the profile resolves.
  const cityNames = propertyAccessCities.length ? propertyAccessCities : DEFAULT_PROPERTY_CITIES;
  const cities = cityNames.map((name) => ({
    value: name,
    label: t(name, CITY_LABELS_GU[name] || name),
  }));

  const checkSession = async () => {
    try {
      const token = Cookies.get("token");
      if (!token) return;

      const sessionResponse = await apiClient(
        "get",
        "/cjidnvij/ceksfbuebijn/user/v2/checkSession",
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      let finalData = sessionResponse;
      if (sessionResponse?.error?.encrypted) {
        const decrypted = decryptResponse(sessionResponse.error.encrypted);
        if (decrypted) finalData = decrypted;
      }

      if (
        finalData.success === false ||
        finalData.status === "error" ||
        finalData.status === 401
      ) {
        if (finalData.action === "logout" || finalData.status === 401) {
          handleLogout(finalData.message || "Session expired");
        } else if (finalData.error?.action === "logout") {
          handleLogout(finalData.error.message || "Session expired");
        }
      }
    } catch (error) {
      console.error("Error checking session:", error);
    }
  };

  const handleLogout = (message) => {
    Cookies.remove("token");
    Cookies.remove("userId");
    Cookies.remove("isPremium");
    Cookies.remove("deviceId");
    localStorage.clear();
    navigate("/login");
    showSessionExpiredPopup(message);
  };

  const fetchAllCounts = async (city = selectedCity) => {
    setLoading(true);
    try {
      const res = await apiClient(
        "get",
        "/cjidnvij/ceksfbuebijn/user/allCount",
        { city }
      );
      if (res.success) {
        setAllCounts(res.data?.data || res.data || null);
      }
    } catch (error) {
      console.error("Error fetching broker counts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCityChange = (city) => {
    setSelectedCity(city);
    localStorage.setItem("selectedCity", city); // persist so other pages use the same city
    fetchAllCounts(city);
  };

  // Auto-switch to the first accessible city if the current one isn't in PropertyAccessCity.
  useEffect(() => {
    if (propertyAccessCities.length === 0) return; // no restriction / not loaded yet
    if (hasCityAccess(selectedCity)) return; // current selection already allowed
    const firstAllowed = cities.find((c) => hasCityAccess(c.value));
    if (firstAllowed && firstAllowed.value !== selectedCity) {
      handleCityChange(firstAllowed.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyAccessCities.join("|")]);

  useEffect(() => {
    checkSession();
    fetchAllCounts();

    // Fetch my properties count
    const fetchMyPropertiesCount = async () => {
      try {
        const token = Cookies.get("token");
        const result = await apiClient(
          "GET",
          "/cjidnvij/ceksfbuebijn/user/brokerproperties/my-properties?listedBy=broker",
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (result.success) {
          const data = result.data;
          const properties = data?.data || data || [];
          setMyPropertiesCount(properties.length || 0);
        }
      } catch (error) {
        console.error("Error fetching my properties count:", error);
      }
    };

    // Fetch my requirements count
    const fetchMyRequirementsCount = async () => {
      try {
        const token = Cookies.get("token");
        const result = await apiClient(
          "GET",
          "/cjidnvij/ceksfbuebijn/user/brokerrequirements/my-requirements",
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (result.success) {
          const data = result.data;
          const requirements = data?.data || data || [];
          setMyRequirementsCount(requirements.length || 0);
        }
      } catch (error) {
        console.error("Error fetching my requirements count:", error);
      }
    };

    fetchMyPropertiesCount();
    fetchMyRequirementsCount();
  }, []);

  const handlePropertyClick = (type, status, listedBy) => {
    const isLoggedIn = Cookies.get("userId");
    const isPremium = Cookies.get("isPremium");
    window.scrollTo(0, 0);

    if (listedBy === "broker") {
      navigate(`/residential-rental/${listedBy}`, {
        state: { type, status, listedBy, scrollToTop: true },
      });
    } else if (listedBy === "client") {
      navigate(`/residential-rental/requirement`, {
        state: { type, status, listedBy, scrollToTop: true },
      });
    } else {
      if (isPremium === "1") {
        navigate("/residential-rental", {
          state: { type, status, listedBy, scrollToTop: true },
        });
      } else if (isLoggedIn && isPremium === "0") {
        openPremiumRequiredPopup();
      } else {
        navigate("/login");
      }
    }
  };

  const agentRequirements = [
    { en: "Residential Rent", gu: "રહેણાંક ભાડે", val: allCounts?.brokerRequirements?.byType?.["Residential Rent"] || 0, img: "/image/residental-rent.png", icon: Building, bg: "bg-[hsl(30,100%,90%)]", color: "text-[hsl(30,100%,45%)]" },
    { en: "Residential Sell", gu: "રહેણાંક વેચાણ", val: allCounts?.brokerRequirements?.byType?.["Residential Sell"] || 0, img: "/image/2.png", icon: TrendingUp, bg: "bg-[hsl(90,50%,88%)]", color: "text-[hsl(90,50%,35%)]" },
    { en: "Commercial Rent", gu: "કોમર્શિયલ ભાડે", val: allCounts?.brokerRequirements?.byType?.["Commercial Rent"] || 0, img: "/image/3.png", icon: Store, bg: "bg-[hsl(170,60%,88%)]", color: "text-[hsl(170,60%,35%)]" },
    { en: "Commercial Sell", gu: "કોમર્શિયલ વેચાણ", val: allCounts?.brokerRequirements?.byType?.["Commercial Sell"] || 0, img: "/image/4.png", icon: Store, bg: "bg-[hsl(210,80%,90%)]", color: "text-[hsl(210,80%,45%)]" },
  ];

  const brokerProperties = [
    { en: "Total Property", gu: "કુલ પ્રોપર્ટી", val: allCounts?.brokerProperties?.total || 0, img: "/image/5.png", icon: Home, bg: "bg-[hsl(262,83%,92%)]", color: "text-primary" },
    { en: "Residential Rent", gu: "રહેણાંક ભાડે", val: allCounts?.brokerProperties?.byType?.["Residential Rent"] || 0, img: "/image/residental-rent.png", icon: Building, bg: "bg-[hsl(30,100%,90%)]", color: "text-[hsl(30,100%,45%)]" },
    { en: "Residential Sell", gu: "રહેણાંક વેચાણ", val: allCounts?.brokerProperties?.byType?.["Residential Sell"] || 0, img: "/image/2.png", icon: TrendingUp, bg: "bg-[hsl(90,50%,88%)]", color: "text-[hsl(90,50%,35%)]" },
    { en: "Commercial Rent", gu: "કોમર્શિયલ ભાડે", val: allCounts?.brokerProperties?.byType?.["Commercial Rent"] || 0, img: "/image/3.png", icon: Store, bg: "bg-[hsl(170,60%,88%)]", color: "text-[hsl(170,60%,35%)]" },
    { en: "Commercial Sell", gu: "કોમર્શિયલ વેચાણ", val: allCounts?.brokerProperties?.byType?.["Commercial Sell"] || 0, img: "/image/4.png", icon: Store, bg: "bg-[hsl(210,80%,90%)]", color: "text-[hsl(210,80%,45%)]" },
  ];

  const mappedBhkData = allCounts?.bhkDistribution
    ? (() => {
      let b1 = 0, b2 = 0, b3 = 0, b4Plus = 0;
      Object.entries(allCounts.bhkDistribution).forEach(([key, value]) => {
        const match = key.match(/\d+/);
        if (match) {
          const num = parseInt(match[0], 10);
          if (num === 1) b1 += value;
          else if (num === 2) b2 += value;
          else if (num === 3) b3 += value;
          else if (num >= 4) b4Plus += value;
        }
      });
      return [
        { name: "1 BHK", value: b1 },
        { name: "2 BHK", value: b2 },
        { name: "3 BHK", value: b3 },
        { name: "4+ BHK", value: b4Plus },
      ];
    })()
    : bhkData;

  const mappedSubtypeData = allCounts?.unitTypeDistribution
    ? ["Apartment", "Office", "Bungalow", "Flat"].map(name => ({
      name,
      count: allCounts.unitTypeDistribution[name] || 0
    }))
    : subtypeData;

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        {/* On mobile the search button sits to the right of the title and the
            cities drop to their own scrollable row; from md up all three share
            one row again (title left, cities + search right). */}
        <div className="flex items-center justify-between gap-x-3 gap-y-2 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => navigate(-1)}
              className="md:hidden p-1 -ml-1 rounded-full hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft size={24} />
            </button>
            <h1 className="text-xl md:text-2xl font-bold truncate">{t("Broker Properties", "બ્રોકર પ્રોપર્ટી")}</h1>
          </div>
          <Button
            size="sm"
            className="order-2 md:order-3 ml-auto md:ml-0 shrink-0 gradient-primary border-0 text-primary-foreground gap-1.5 shadow-md"
            onClick={() => navigate("/filter")}
          >
            <Search size={18} /><span className="md:hidden">Search</span>
            <span className="hidden md:inline">{t("Search Property", "પ્રોપર્ટી શોધો")}</span>
          </Button>
          <div className="order-3 md:order-2 w-full md:w-auto md:ml-auto min-w-0 max-w-full">
            <div className="flex items-center gap-1.5 flex-nowrap min-w-0 max-w-full overflow-x-auto py-1 -my-1">
              {cities.map((city) => {
                const cityAllowed = hasCityAccess(city.value);
                return (
                  <Button
                    key={city.value}
                    size="sm"
                    variant={selectedCity === city.value ? "default" : "outline"}
                    disabled={!cityAllowed}
                    title={!cityAllowed ? t("No access to this city", "આ શહેરની ઍક્સેસ નથી") : undefined}
                    className={`h-8 px-2 text-[11px] lg:h-9 lg:px-3 lg:text-sm whitespace-nowrap shrink-0 ${
                      selectedCity === city.value
                        ? "gradient-primary border-0 text-primary-foreground shadow-md"
                        : ""
                    } ${!cityAllowed ? "opacity-50 cursor-not-allowed" : ""}`}
                    onClick={() => cityAllowed && handleCityChange(city.value)}
                  >
                    {city.label}
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Agent Requirements */}
      <div className={`space-y-3 ${!hasFeature(FEATURES.AGENT_REQUIREMENT) ? "opacity-75" : ""}`}>
        <div className="flex items-center justify-center gap-2">
          <h2 className="text-lg font-bold text-center underline decoration-primary decoration-2 underline-offset-4">{t("Agent Requirements", "એજન્ટ જરૂરિયાત")}</h2>
          {!hasFeature(FEATURES.AGENT_REQUIREMENT) && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 font-bold uppercase tracking-wider flex items-center gap-1">
              {t("Locked", "લોક")}
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {agentRequirements.map((item) => (
            <div
              key={item.en}
              className={`glass-card rounded-xl p-3 md:p-5 flex items-center gap-3 md:gap-4 border-2 border-primary/20 transition-all duration-200 
                ${!hasFeature(FEATURES.AGENT_REQUIREMENT) 
                  ? "cursor-not-allowed grayscale-[0.5]" 
                  : "cursor-pointer hover:border-primary/40"}`}
              onClick={() => hasFeature(FEATURES.AGENT_REQUIREMENT) && handlePropertyClick(item.en, "active", "client")}
            >
              <div className={`w-9 lg:w-10 xl:w-12 h-9 lg:h-10 xl:h-12 rounded-full ${item.img ? "" : item.bg} flex items-center justify-center flex-shrink-0 overflow-hidden`}>
                {item.img ? (
                  <img src={item.img} alt={item.en} className="w-full h-full object-contain" />
                ) : (
                  <item.icon size={20} className="lg:w-6 lg:h-6 xl:w-6 xl:h-6" />
                )}
              </div>
              <div>
                {loading ? (
                  <div className="skeleton-shimmer rounded-md h-5 w-12 mb-1" />
                ) : (
                  <p className="text-base lg:text-lg xl:text-xl font-bold">#{item.val?.toLocaleString() || "0"}</p>
                )}
                <p className="text-[10px] lg:text-xs text-muted-foreground">{t(item.en, item.gu)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Broker Properties */}
      <div className={`space-y-3 ${!hasFeature(FEATURES.BROKER_PROPERTY) ? "opacity-75" : ""}`}>
        <div className="flex items-center justify-center gap-2">
          <h2 className="text-lg font-bold text-center underline decoration-primary decoration-2 underline-offset-4">{t("Broker Properties", "બ્રોકર પ્રોપર્ટી")}</h2>
          {!hasFeature(FEATURES.BROKER_PROPERTY) && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 font-bold uppercase tracking-wider flex items-center gap-1">
              {t("Locked", "લોક")}
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {brokerProperties.map((item) => {
            const isTotal = item.en === "Total Property";
            const isLocked = !hasFeature(FEATURES.BROKER_PROPERTY);
            return (
              <div
                key={item.en}
                className={`glass-card rounded-xl p-3 md:p-5 flex transition-all duration-200 border-2 border-primary/20 
                  ${isLocked 
                    ? "cursor-not-allowed grayscale-[0.5]" 
                    : "cursor-pointer hover:border-primary/40"} 
                  ${isTotal
                    ? "col-span-2 lg:col-span-1 flex-row items-center justify-center lg:justify-start gap-4"
                    : "items-center gap-3 md:gap-4"}`}
                onClick={() => !isLocked && handlePropertyClick(isTotal ? "All" : item.en, "active", "broker")}

              >
                <div className={`${isTotal ? "w-12 lg:w-12 h-12 lg:h-12" : "w-9 lg:w-10 xl:w-12 h-9 lg:h-10 xl:h-12"} rounded-full ${item.img ? "" : item.bg} flex items-center justify-center flex-shrink-0 overflow-hidden`}>
                  {item.img ? (
                    <img src={item.img} alt={item.en} className="w-full h-full object-contain" />
                  ) : (
                    <item.icon size={isTotal ? 26 : 20} className="lg:w-6 lg:h-6 xl:w-8 xl:h-8" />
                  )}
                </div>
                <div>
                  {loading ? (
                    <div className={`skeleton-shimmer rounded-md ${isTotal ? "h-6 w-16" : "h-5 w-12"} mb-1`} />
                  ) : (
                    <p className={`${isTotal ? "text-xl lg:text-lg xl:text-xl" : "text-base lg:text-lg xl:text-xl"} font-bold`}>#{item.val?.toLocaleString() || "0"}</p>
                  )}
                  <p className={`${isTotal ? "text-xs lg:text-[10px] xl:text-xs" : "text-[10px] lg:text-xs"} text-muted-foreground`}>{t(item.en, item.gu)}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* My Properties & My Requirements */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
        <div className="glass-card glass-card-hover rounded-xl p-3 lg:p-5 flex items-center gap-3 lg:gap-4 cursor-pointer transition-all duration-200" onClick={() => navigate("/MyProperty")}>
          <div className="w-11 lg:w-14 h-11 lg:h-14 rounded-full bg-[hsl(262,83%,92%)] flex items-center justify-center">
            <Home size={22} className="lg:w-7 lg:h-7 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-sm lg:text-base">{t("My Properties", "મારી પ્રોપર્ટી")}</h3>
            <p className="text-xs lg:text-sm text-muted-foreground">{t(`${myPropertiesCount} listed properties`, `${myPropertiesCount} લિસ્ટેડ પ્રોપર્ટી`)}</p>
          </div>
        </div>
        <div className="glass-card glass-card-hover rounded-xl p-3 lg:p-5 flex items-center gap-3 lg:gap-4 cursor-pointer transition-all duration-200" onClick={() => navigate("/MyProperty", { state: { activeTab: "requirements" } })}>
          <div className="w-11 lg:w-14 h-11 lg:h-14 rounded-full bg-[hsl(30,100%,90%)] flex items-center justify-center">
            <ClipboardList size={22} className="lg:w-7 lg:h-7 text-[hsl(30,100%,45%)]" />
          </div>
          <div>
            <h3 className="font-semibold text-sm lg:text-base">{t("My Requirements", "મારી જરૂરિયાત")}</h3>
            <p className="text-xs lg:text-sm text-muted-foreground">{t(`${myRequirementsCount} listed requirements`, `${myRequirementsCount} લિસ્ટેડ જરૂરિયાત`)}</p>
          </div>
        </div>
      </div>

      {/* Quick Shortcuts */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
        <button onClick={() => navigate("/property-listed")} className="glass-card glass-card-hover rounded-xl p-3 lg:p-5 flex items-center gap-3 lg:gap-4 transition-all duration-200">
          <div className="w-11 lg:w-14 h-11 lg:h-14 rounded-full bg-[hsl(170,60%,88%)] flex items-center justify-center">
            <ListPlus size={22} className="lg:w-7 lg:h-7 text-[hsl(170,60%,35%)]" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-sm lg:text-base">{t("List Your Requirement", "તમારી જરૂરિયાત લિસ્ટ કરો")}</h3>
            <p className="text-xs lg:text-sm text-muted-foreground">{t("Post what your client needs", "તમારા ક્લાયન્ટની જરૂરિયાત પોસ્ટ કરો")}</p>
          </div>
        </button>
        <button onClick={() => navigate("/property-listed")} className="glass-card glass-card-hover rounded-xl p-3 lg:p-5 flex items-center gap-3 lg:gap-4 transition-all duration-200">
          <div className="w-11 lg:w-14 h-11 lg:h-14 rounded-full bg-[hsl(90,50%,88%)] flex items-center justify-center">
            <PlusCircle size={22} className="lg:w-7 lg:h-7 text-[hsl(90,50%,35%)]" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-sm lg:text-base">{t("List Your Property", "તમારી પ્રોપર્ટી લિસ્ટ કરો")}</h3>
            <p className="text-xs lg:text-sm text-muted-foreground">{t("Add a property listing", "પ્રોપર્ટી લિસ્ટિંગ ઉમેરો")}</p>
          </div>
        </button>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-4">
        <div className="glass-card rounded-xl p-3 lg:p-5">
          <h3 className="font-semibold mb-3 lg:mb-4 text-sm lg:text-base">{t("BHK Distribution", "BHK વિતરણ")}</h3>
          <ResponsiveContainer width="100%" height={220} className="lg:!h-[250px]">
            <PieChart>
              <Pie data={mappedBhkData} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                {mappedBhkData.map((_, i) => (<Cell key={i} fill={COLORS[i % COLORS.length]} />))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="glass-card rounded-xl p-3 lg:p-5">
          <h3 className="font-semibold mb-3 lg:mb-4 text-sm lg:text-base">{t("Property Subtype Distribution", "પ્રોપર્ટી સબટાઇપ વિતરણ")}</h3>
          <ResponsiveContainer width="100%" height={220} className="lg:!h-[250px]">
            <BarChart data={mappedSubtypeData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis type="number" fontSize={12} />
              <YAxis dataKey="name" type="category" fontSize={12} width={90} />
              <Tooltip />
              <Bar dataKey="count" fill="hsl(262, 83%, 58%)" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default BrokerB2B;
